import { ReactNode } from 'react';
import styled from 'styled-components';

import { SupplierSubscriptionPlan } from '../../../types/SupplierGroupSubscriptionMessage';
import { Button } from '../../cdl/Button/Button';
import { Text } from '../../cdl/Text/Text';
import { useEnums } from '../../common/hooks/useEnums';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { getSupplierSubscriptionPlanActionLabel } from '../getSupplierSubscriptionPlanActionLabel';

import { SupplierSubscriptionPlanFeatures } from './SupplierSubscriptionPlanFeatures';

const Card = styled.section<{ $featured: boolean }>`
    display: grid;
    grid-template-rows: 88px 160px 64px auto;
    gap: ${theme.space[16]};
    padding: ${theme.space[16]};
    border: 1px solid ${({ $featured }) => ($featured ? theme.colors.accent.emphasis : theme.colors.border.default)};
    border-radius: ${theme.radii[8]};
    background: ${({ $featured }) => ($featured ? theme.colors.accent.background : theme.colors.background.default)};

    @media (max-width: 760px) {
        grid-template-rows: auto;
    }
`;

interface SupplierSubscriptionPlanCardProps {
    plan: SupplierSubscriptionPlan;
    currentPlan: SupplierSubscriptionPlan;
    description: string;
    children: ReactNode;
}

export const SupplierSubscriptionPlanCard = ({
    plan,
    currentPlan,
    description,
    children,
}: SupplierSubscriptionPlanCardProps) => {
    const { getHumanReadableValue } = useEnums();
    const planLabel = getHumanReadableValue('supplierSubscriptionPlan', plan);
    const isNext =
        (currentPlan === SupplierSubscriptionPlan.BASIC && plan === SupplierSubscriptionPlan.PROFESSIONAL) ||
        (currentPlan === SupplierSubscriptionPlan.PROFESSIONAL && plan === SupplierSubscriptionPlan.ENTERPRISE);
    const actionLabel = getSupplierSubscriptionPlanActionLabel(plan, currentPlan, planLabel);

    return (
        <Card $featured={isNext}>
            <Box display="flex" flexDirection="column" gap={8}>
                <Text as="h3" variant="subtitle1">
                    {planLabel}
                </Text>
                <Text variant="small" color="foreground.muted">
                    {description}
                </Text>
            </Box>
            {children}
            <Box display="flex" flexDirection="column" alignItems="center" gap={8}>
                <Button type="button" emphasis={isNext ? 'high' : 'medium'} width="100%" disabled>
                    {actionLabel}
                </Button>
            </Box>
            <SupplierSubscriptionPlanFeatures plan={plan} />
        </Card>
    );
};
